import type { JSX } from 'solid-js';
import { t } from '../i18n';
import type { ArtifactFilter } from '../lib/artifacts';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

// The three coordinates `GET /v1/artifacts` narrows on. Presenter — the container
// owns the applied filter, which is part of the query key, so applying re-reads
// SERVER-side. Filtering rows the client happens to hold would hide everything
// past the head that read returned.
//
// It APPLIES ON SUBMIT rather than per keystroke: the list is a bounded head, so
// a half-typed run id would spend a round trip answering a question nobody asked.
// Clear is a separate button because a form reset restores the applied values
// without telling the container anything.

/** Trim to `undefined` — an empty box means "not narrowed", never `?run_id=`. */
function trimmed(value: FormDataEntryValue | null): string | undefined {
  const text = typeof value === 'string' ? value.trim() : '';
  return text === '' ? undefined : text;
}

export function ArtifactFilterBar(props: {
  readonly value: ArtifactFilter;
  readonly onApply: (filter: ArtifactFilter) => void;
}): JSX.Element {
  const submit = (event: SubmitEvent & { currentTarget: HTMLFormElement }): void => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    props.onApply({
      run_id: trimmed(data.get('run_id')),
      task_id: trimmed(data.get('task_id')),
      repo_id: trimmed(data.get('repo_id')),
    });
  };

  return (
    <form class="filter-row" onSubmit={submit}>
      <Field label={t('artifacts.filter_run')} id="artifact-filter-run">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            name="run_id"
            value={props.value.run_id ?? ''}
            placeholder={t('artifacts.filter_run_placeholder')}
          />
        )}
      </Field>
      <Field label={t('artifacts.filter_task')} id="artifact-filter-task">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            name="task_id"
            value={props.value.task_id ?? ''}
            placeholder={t('artifacts.filter_task_placeholder')}
          />
        )}
      </Field>
      <Field label={t('artifacts.filter_repo')} id="artifact-filter-repo">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            name="repo_id"
            value={props.value.repo_id ?? ''}
            placeholder={t('artifacts.filter_repo_placeholder')}
          />
        )}
      </Field>
      <button type="submit" class={buttonClass({ variant: 'outline', size: 'sm' })}>
        {t('artifacts.filter_apply')}
      </button>
      <Button variant="ghost" size="sm" onClick={() => props.onApply({})}>
        {t('artifacts.filter_clear')}
      </Button>
    </form>
  );
}
